import { collection, addDoc, serverTimestamp, query, where, orderBy, onSnapshot, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { COLLECTIONS } from '../../constants/collections';
import { getTenantUsers } from '../firestore/userService';
import { getTenantPermissions } from '../firestore/permissionService';
import { NOTIFICATION_TYPES } from '../../constants/notifications';

/**
 * Creates notifications for relevant users when a reservation is updated.
 */
export const createNotificationsForChange = async (tenantId, reservation, fieldChanges, performedBy) => {
    try {
        if (!tenantId || !reservation || !performedBy) return;

        // 1. Get all users in the tenant
        const users = await getTenantUsers(tenantId);

        // 2. Get notification preferences for the tenant
        const { notificationPreferences } = await getTenantPermissions(tenantId);

        const notifications = [];
        const actorUid = typeof performedBy === 'string' ? performedBy : (performedBy.uid || performedBy.id);

        for (const user of users) {
            // Rule: Don't notify the person who made the change
            if (user.id === actorUid) continue;

            // Rule: Check scoped access (for transporters/truck drivers)
            // Transporters only care about their own reservations
            if (user.transporterId && user.transporterId !== reservation.transporterId) {
                continue;
            }

            // Rule: Check role preferences
            const rolePrefs = notificationPreferences[user.role] || [];

            // Check each change type provided in fieldChanges
            // fieldChanges structure: { [NOTIFICATION_TYPES.TIME_CHANGE]: { old, new }, ... }
            for (const [type, data] of Object.entries(fieldChanges)) {
                if (rolePrefs.includes(type)) {
                    notifications.push({
                        userId: user.id,
                        type: type,
                        reservationId: reservation.id,
                        plateNumber: reservation.plateNumber || 'PLAKA YOK',
                        oldValue: data.old,
                        newValue: data.new,
                        performedBy: {
                            uid: actorUid,
                            name: performedBy.fullName || performedBy.name || 'Sistem',
                            companyName: performedBy.companyName || performedBy.transporterName || performedBy.tenantName || ''
                        },
                        read: false,
                        timestamp: serverTimestamp()
                    });
                }
            }
        }

        if (notifications.length === 0) return;

        // Batch create notifications
        const notificationPromises = notifications.map(notif =>
            addDoc(collection(db, `tenants/${tenantId}/${COLLECTIONS.NOTIFICATIONS}`), notif)
        );

        await Promise.all(notificationPromises);

    } catch (error) {
        console.error('Error creating notifications:', error);
    }
};

/**
 * Get notifications for a user with real-time updates
 */
export const subscribeToNotifications = (tenantId, userId, callback) => {
    if (!tenantId || !userId) return () => { };

    const notificationsRef = collection(db, `tenants/${tenantId}/${COLLECTIONS.NOTIFICATIONS}`);
    const q = query(
        notificationsRef,
        where('userId', '==', userId)
    );

    return onSnapshot(q, (snap) => {
        const data = snap.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            timestamp: doc.data().timestamp?.toDate() || new Date()
        })).sort((a, b) => b.timestamp - a.timestamp);
        callback(data);
    }, (error) => {
        console.error('Error subscribing to notifications:', error);
    });
};

/**
 * Mark a notification as read
 */
export const markNotificationAsRead = async (tenantId, notificationId) => {
    try {
        if (!tenantId || !notificationId) return;
        const docRef = doc(db, `tenants/${tenantId}/${COLLECTIONS.NOTIFICATIONS}`, notificationId);
        await updateDoc(docRef, { read: true });
    } catch (error) {
        console.error('Error marking notification as read:', error);
    }
};

/**
 * Mark all notifications as read for a user
 */
export const markAllAsRead = async (tenantId, userId) => {
    try {
        if (!tenantId || !userId) return;
        const notificationsRef = collection(db, `tenants/${tenantId}/${COLLECTIONS.NOTIFICATIONS}`);
        const q = query(notificationsRef, where('userId', '==', userId), where('read', '==', false));
        const snap = await getDocs(q);

        const batchPromises = snap.docs.map(d => updateDoc(d.ref, { read: true }));
        await Promise.all(batchPromises);
    } catch (error) {
        console.error('Error marking all notifications as read:', error);
    }
};
